import { useState, useEffect } from 'react';
import { Paper, TextField, Box, Typography, IconButton, Chip } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';
import { getIcon } from '../../../utils/IconHandler';
import { formatDate } from '../../../utils/DateUtils';
import PageHeader from '../../../components/PageHeader/PageHeader';
import TableRefreshButton from '../../../components/TableRefreshButton/TableRefreshButton';
import ViewB2BOrderModal from '../orders/ViewB2BOrderModal';
import { B2BOrderService } from '../../../services/B2BServices';
import B2BUtilsService from '../../../services/B2BUtilsService';
import dayjs from 'dayjs';
import { formatMoney } from '../../../utils/currency';

const AllOrdersPage = () => {
  const { t } = useTranslation();

  const location = useLocation();
  const { clientId: clientIdFromParams } = useParams();
  
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });


  const fetchOrders = async () => {
    console.log('🔍 AllOrdersPage - fetchOrders called');
    try {
      setLoading(true);
      
      // Verificar si hay clientData en el state de la navegación o en los params de la URL
      const clientData = location.state?.clientData;
      const clientId = clientData?.clientId || clientIdFromParams;
      
      console.log('🔍 AllOrdersPage - clientData:', clientData);
      console.log('🔍 AllOrdersPage - clientIdFromParams:', clientIdFromParams);
      console.log('🔍 AllOrdersPage - final clientId:', clientId);
      
      let response;
      
      if (clientId) {
        // Si hay clientId, hacer fetch específico del cliente
        console.log('🔍 AllOrdersPage - Fetching orders for client:', clientId);
        response = await B2BOrderService.getOrdersByClient(clientId, 1, 100, '');
      } else {
        // Si no hay clientId, hacer fetch general
        console.log('🔍 AllOrdersPage - Fetching all orders');
        response = await B2BOrderService.getOrders(1, 100, '');
      }
      
      console.log('🔍 AllOrdersPage - Response received:', response);
      console.log('🔍 AllOrdersPage - Response type:', typeof response);
      console.log('🔍 AllOrdersPage - Is array:', Array.isArray(response));
      
      // Manejar la estructura de respuesta del backend
      if (Array.isArray(response)) {
        // Si la respuesta es un array, usarla directamente
        setOrders(response);
      } else if (response && response.orders) {
        // Si la respuesta tiene un campo orders, usarlo
        console.log('🔍 AllOrdersPage - Using response.orders:', response.orders);
        setOrders(response.orders || []);
      } else {
        // Si no es array ni tiene orders, usar fallback
        console.log('🔍 AllOrdersPage - Response is not array and has no orders, using fallback');
        setOrders([]);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log('🔍 AllOrdersPage - useEffect triggered');
    console.log('🔍 AllOrdersPage - clientIdFromParams:', clientIdFromParams);
    console.log('🔍 AllOrdersPage - location.state:', location.state);
    fetchOrders();
  }, [clientIdFromParams, location.state]);

  const handleViewOrder = (order) => {
    setSelectedOrder(order);
    setIsViewModalOpen(true);
  };

  const formatCurrency = (amount) => {
    const numericAmount = Number(amount);
    const safeAmount = Number.isFinite(numericAmount) ? numericAmount : 0;
    return formatMoney(safeAmount);
  };

  const normalizedOrders = orders.map((order) => {
    const totalAmount = Number(order.totalAmount ?? order.amount ?? 0);
    const paidAmount = Number(order.paidAmount ?? 0);
    const creditDays = Number(order.creditDays ?? 0);
    const derivedDueDate = !order.dueDate && order.orderDate && creditDays > 0
      ? dayjs(order.orderDate).add(creditDays, 'day').toISOString()
      : order.dueDate;

    return {
      ...order,
      totalAmount: Number.isFinite(totalAmount) ? totalAmount : 0,
      paidAmount: Number.isFinite(paidAmount) ? paidAmount : 0,
      dueDate: derivedDueDate || null,
      financialStatus: order.paymentStatus || 'UNPAID'
    };
  });

  const columns = [
    { 
      field: 'id', 
      headerName: t('order_number'), 
      flex: 1,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
          #{params.value}
        </Typography>
      )
    },
    {
      field: 'financialStatus',
      headerName: t('payment_status'),
      flex: 1,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Chip
          label={B2BUtilsService.getBillingOrderStatusText(params.value, params.row.dueDate, t)}
          color={B2BUtilsService.getBillingOrderStatusColor(params.value, params.row.dueDate)}
          size="small"
        />
      )
    },
    { 
      field: 'totalAmount', 
      headerName: t('amount'), 
      type: 'number', 
      flex: 1, 
      align: 'center', 
      headerAlign: 'center',
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
          {formatCurrency(params.value)}
        </Typography>
      )
    },
    { 
      field: 'paidAmount', 
      headerName: t('paid_amount'), 
      type: 'number', 
      flex: 1, 
      align: 'center', 
      headerAlign: 'center',
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'success.main' }}>
          {formatCurrency(params.value)}
        </Typography>
      )
    },
    { 
      field: 'orderDate', 
      headerName: t('order_date'), 
      flex: 1.2, 
      align: 'center', 
      headerAlign: 'center',
      renderCell: (params) => {
        if (!params.value) return '—';
        const date = dayjs(params.value);
        return (
          <Box sx={{ textAlign: 'center' }}>
            <div>{formatDate(date)}</div>
          </Box>
        );
      }
    },
    { 
      field: 'dueDate', 
      headerName: t('due_date'), 
      flex: 1.2, 
      align: 'center', 
      headerAlign: 'center',
      renderCell: (params) => {
        if (!params.value) return '—';
        const date = dayjs(params.value);
        return (
          <Box sx={{ textAlign: 'center' }}>
            <div>{formatDate(date)}</div>
          </Box>
        );
      }
    },
    {
      field: 'actions',
      headerName: t('actions'),
      flex: 1,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          <IconButton 
            onClick={() => handleViewOrder(params.row)}
            sx={{ borderRadius: '50%' }}
            title={t('view_details')}
          >
            {getIcon('eye')}
          </IconButton>
        </Box>
      )
    }
  ];

  const filteredOrders = normalizedOrders.filter(order =>
    order.id?.toString().toLowerCase().includes(searchTerm.toLowerCase()) ||
    order.financialStatus?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    order.totalAmount?.toString().includes(searchTerm.toLowerCase())
  );

  // Obtener información del cliente si está disponible
  const clientData = location.state?.clientData;
  const clientId = clientData?.clientId || clientIdFromParams;
  
  const pageTitle = clientData?.clientName 
    ? `${t('all_orders')} - ${clientData.clientName}`
    : clientIdFromParams 
      ? `${t('all_orders')} - Cliente ${clientIdFromParams}`
      : t('all_orders');
  const pageSubtitle = clientData?.clientName 
    ? t('complete_order_history')
    : clientIdFromParams 
      ? `Historial completo de pedidos - Cliente ${clientIdFromParams}`
      : t('complete_order_history');

  return (
    <Box p={3}>
      <PageHeader 
        title={pageTitle} 
        subtitle={pageSubtitle}
      />
      
      
      {/* Tabla de todos los pedidos */}
      <Paper sx={{ padding: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              label={t('search')}
              variant="outlined"
              size="small"
              sx={{ width: 300 }}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </Box>
          
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Typography variant="body2" color="textSecondary">
              {filteredOrders.length} {t('orders')}
            </Typography>
            <TableRefreshButton loading={loading} onClick={fetchOrders} />
          </Box>
        </Box>

        <Box sx={{ overflowX: 'auto' }}>
          <Box sx={{ minWidth: 1000 }}>
            <DataGrid
              loading={loading}
              rows={filteredOrders}
              columns={columns}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={[10, 25, 50]}
              getRowId={(row) => row.id}
              sx={{
                '& .MuiDataGrid-cell': {
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }
              }}
            />
          </Box>
        </Box>
      </Paper>

      {/* Modales */}
      <ViewB2BOrderModal
        open={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        order={selectedOrder}
      />
    </Box>
  );
};

export default AllOrdersPage;
